import React, { useEffect, useState } from 'react';
import { Form, Drawer, Button, Space, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  shareCompanyFormEnum,
  ICompanyShareFormValues,
} from 'app/containers/Company/slice/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanyDetails,
  selectShareCompanyFormState,
} from 'app/containers/Company/slice/selectors';
import { useCompanySlice } from 'app/containers/Company/slice';
import { selectSelectedEnv } from 'app/containers/HiikoEnv/slice/selectors';
import { checkIsPositiveNumber } from 'utils/validationHelpers';
import NumberInput from 'app/components/NumberInput/NumberInput';
import PermissionsRadioButtons from 'app/containers/User/parts/PermissionsRadioButtons/PermissionsRadioButtons';
import { IAccessRightsNumbers } from 'app/containers/User/slice/types';

const CompanyShareForm = () => {
  const { t } = useTranslation();

  const { isOpen, type, id, loading, permissions, deleteLoading } = useSelector(
    selectShareCompanyFormState,
  );

  const selectedEnv = useSelector(selectSelectedEnv);
  const { company } = useSelector(selectCompanyDetails);

  const isShareFormType = type === 'share';

  const { actions } = useCompanySlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [
    checkedPermissions,
    setCheckedPermissions,
  ] = useState<IAccessRightsNumbers | null>(null);

  // Set edit access data on open
  useEffect(() => {
    if (permissions && !isShareFormType && form && isOpen && id) {
      form.setFieldsValue({
        [shareCompanyFormEnum.id]: id,
        ...permissions,
      });
      setCheckedPermissions(permissions);
    }
  }, [permissions, isShareFormType, isOpen, form, id]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setCheckedPermissions(null);
    }
  }, [isOpen, form]);

  const onFinish = (values: ICompanyShareFormValues) => {
    if (!selectedEnv || !company) {
      return;
    }
    const { id, ...formPermissions } = values;
    dispatch(
      actions.shareCompany({
        envId: selectedEnv.id,
        companyId: company.id,
        permissions: formPermissions,
        id,
      }),
    );
  };

  const onCloseForm = () => {
    dispatch(actions.toggleShareCompanyForm(false));
  };

  const onDeleteCompanySharing = () => {
    if (id && selectedEnv && company) {
      dispatch(
        actions.deleteShareCompany({
          guestEnvId: id,
          companyId: company.id,
          envId: selectedEnv.id,
        }),
      );
    }
  };

  const onChange = (
    changedItem: { [key: string]: number | string },
    values: ICompanyShareFormValues,
  ) => {
    const { id, ...checkboxValues } = values;
    setCheckedPermissions(checkboxValues);
  };

  return (
    <Drawer
      title={
        isShareFormType
          ? t('Company.ShareForm.ShareCompany', 'Share Company')
          : t(
              'Company.ShareForm.EditCompanyAccessRights',
              'Edit Company Access Rights',
            )
      }
      visible={isOpen}
      forceRender={true}
      onClose={onCloseForm}
      destroyOnClose
      placement="right"
      width={430}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onCloseForm}>
              {t('Company.ShareForm.Cancel', 'Cancel')}
            </Button>
            {!isShareFormType && (
              <Button
                onClick={onDeleteCompanySharing}
                danger={true}
                type="primary"
                loading={deleteLoading}
              >
                {t('Company.ShareForm.RevokeAccess', 'Revoke Access')}
              </Button>
            )}

            <Button
              loading={loading}
              form="companyShareForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {isShareFormType
                ? t('Company.ShareForm.ShareCompany', 'Share Company')
                : t('Company.ShareForm.Save', 'Save')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark="optional"
        id="companyShareForm"
        onFinish={onFinish}
        onValuesChange={onChange}
      >
        <Form.Item
          label={t(
            'Company.ShareForm.EnvIdNumber',
            'Environmental Identification Number (ID)',
          )}
          name={shareCompanyFormEnum.id}
          rules={[{ required: true }, { validator: checkIsPositiveNumber }]}
        >
          <NumberInput
            disabled={!isShareFormType}
            placeholder={t(
              'Company.ShareForm.EnvIdNumber',
              'Environmental Identification Number (ID)',
            )}
          />
        </Form.Item>
        <PermissionsRadioButtons
          form={form}
          isOpen={isOpen}
          checkedPermissions={checkedPermissions}
        />
      </Form>
    </Drawer>
  );
};

export default CompanyShareForm;
